/** @jsxImportSource @emotion/react */
/* eslint-disable eqeqeq */
import * as React from "react";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  TimeslotTemplateListItem,
  TimeslotTemplateUpdate,
} from "./model/TimeslotTemplatesSuccessResponse";
import { InputFormField } from "../common/hook-form/index";
import { useForm } from "react-hook-form";
import { OrganizationKeyName } from "../../model/Organization";
import { TableRow } from "../../components/table-components/TableRow";
import { TableCellText } from "../../components/table-components/TableCellText";
import { colors } from "../../components/colors";
import { StyledText } from "../../assets/typography/typography";
import { useUpdateTimeslot } from "./service/shop-api";
import { toastr } from "react-redux-toastr";
import { firstTableCellStyle } from "../../style-util";
import { getErrorMessage } from "../../error-handler";

const schema = yup.object().shape({
  maxOrders: yup.string().required("Required"),
});

interface Props {
  template: TimeslotTemplateListItem;
  organization: OrganizationKeyName;
  shopId: string;
  rowIndex: number;
}

export const TimeslotsListRow: React.FC<Props> = ({
  template,
  organization,
  rowIndex,
  shopId,
}) => {
  const [showSuccess, setShowSuccess] = React.useState(false);
  const [lastUpdated, setLastUpdated] = React.useState<TimeslotTemplateUpdate>(
    template
  );

  const {
    mutateAsync: updateTimeslot,
    isLoading: isLoadingShopTimeslotTemplateUpdate,
  } = useUpdateTimeslot();

  const {
    register,
    formState: { errors, isDirty },
    handleSubmit,
    getValues,
  } = useForm<TimeslotTemplateUpdate>({
    resolver: yupResolver(schema),
    defaultValues: {
      maxOrders: template.maxOrders,
      maxPickupOrders: template.maxPickupOrders,
      maxDeliveryOrders: template.maxDeliveryOrders,
      maxUnits: template.maxUnits,
      preparationTime: template.preparationTime,
      deliveryTime: template.deliveryTime,
    },
  });

  // const deleteTimeslot = (timeslotTemplateId: string) => {
  //   dispatch(
  //     shopActions.unlinkProductFromShopAction(
  //       organization,
  //       shopId,
  //       product.id
  //     )
  //   );
  // };

  const onSubmit = async (data: TimeslotTemplateUpdate) => {
    try {
      setShowSuccess(false);
      await updateTimeslot({
        organizationKeyName: organization,
        id: shopId,
        data,
      });
      setShowSuccess(true);
      setTimeout(() => {
        setShowSuccess(false);
      }, 1000);
    } catch (error: any) {
      const errorMessage = getErrorMessage(error);
      toastr.error("Error", errorMessage);
    }
  };

  const areFieldsSame = (
    first: TimeslotTemplateUpdate,
    second: TimeslotTemplateUpdate
  ) => {
    return (
      first.deliveryTime == second.deliveryTime &&
      first.maxDeliveryOrders == second.maxDeliveryOrders &&
      first.maxOrders == second.maxOrders &&
      first.maxPickupOrders == second.maxPickupOrders &&
      first.maxUnits == second.maxUnits &&
      first.preparationTime == second.preparationTime
    );
  };

  const onFieldBlur = (e: any) => {
    const targetId = e.target.getAttribute("form");
    const newTargetId = e.relatedTarget?.getAttribute("form");

    const formValues = getValues();

    const isDifferentRow = targetId !== newTargetId;
    const didUpdateFields = isDirty && !areFieldsSame(formValues, lastUpdated);

    if (isDifferentRow && didUpdateFields) {
      setLastUpdated(formValues);

      handleSubmit(() =>
        onSubmit({
          ...formValues,
          id: template.id,
        })
      )();
    }
  };

  const fieldsArray = [
    "maxOrders",
    "maxPickupOrders",
    "maxDeliveryOrders",
    "maxUnits",
    "preparationTime",
    "deliveryTime",
  ];

  const forceCustomNavigation = (e: KeyboardEvent, columnIndex: number) => {
    switch (e.key) {
      case "ArrowRight": {
        const nextColumn = document.getElementById(
          `${rowIndex},${columnIndex + 1}`
        ) as HTMLInputElement | null;
        if (!nextColumn) {
          const nextRow = document.getElementById(
            `${rowIndex + 1},0`
          ) as HTMLInputElement | null;
          nextRow?.focus();
          nextRow?.select();
        } else {
          nextColumn.focus();
          nextColumn.select();
        }
        e.preventDefault();
        return;
      }
      case "ArrowLeft": {
        const prevColumn = document.getElementById(
          `${rowIndex},${columnIndex - 1}`
        ) as HTMLInputElement | null;
        if (!prevColumn) {
          const prevRow = document.getElementById(
            `${rowIndex - 1},${fieldsArray.length - 1}`
          ) as HTMLInputElement | null;
          prevRow?.focus();
          prevRow?.select();
        } else {
          prevColumn.focus();
          prevColumn.select();
        }
        e.preventDefault();
        return;
      }
      case "ArrowDown": {
        const nextRow = document.getElementById(
          `${rowIndex + 1},${columnIndex}`
        ) as HTMLInputElement | null;
        nextRow?.focus();
        nextRow?.select();
        e.preventDefault();
        return;
      }
      case "ArrowUp": {
        const prevRow = document.getElementById(
          `${rowIndex - 1},${columnIndex}`
        ) as HTMLInputElement | null;
        prevRow?.focus();
        prevRow?.select();
        e.preventDefault();
        return;
      }
      default: {
        return;
      }
    }
  };
  return (
    <>
      <form id={template.id}></form>
      <TableRow>
        <TableCellText css={firstTableCellStyle}>
          <StyledText
            className={`bodyTextRegular mb-0`}
            color={colors.textPrimary}
          >
            {template.day}
          </StyledText>
        </TableCellText>
        <TableCellText>
          <StyledText
            className={`bodyTextRegular mb-0`}
            color={colors.textPrimary}
          >
            {template.from}
          </StyledText>
        </TableCellText>
        <TableCellText>
          <StyledText
            className={`bodyTextRegular mb-0`}
            color={colors.textPrimary}
          >
            {template.to}
          </StyledText>
        </TableCellText>
        {fieldsArray.map((field: string, columnIndex: number) => {
          return (
            <td key={columnIndex}>
              <InputFormField
                form={template.id}
                formGroup={false}
                errors={errors}
                register={register}
                name={field}
                onBlur={onFieldBlur}
                id={`${rowIndex},${columnIndex}`}
                onKeyDown={(e: KeyboardEvent) =>
                  forceCustomNavigation(e, columnIndex)
                }
              >
                {template[field as keyof TimeslotTemplateListItem]}
              </InputFormField>
            </td>
          );
        })}
        <td className="text-right">
          <div className="btn-group">
            <>
              {/* TODO: Add support for deleting rows <button
                onClick={() => deleteTimeslot(template.id)}
                className="btn btn-sm btn-danger"
              >
                Remove
              </button> */}
              {isLoadingShopTimeslotTemplateUpdate && (
                <div style={{ marginLeft: "20px" }}>Updating</div>
              )}
              {showSuccess && !isLoadingShopTimeslotTemplateUpdate && (
                <div style={{ marginLeft: "20px" }}>Updated!</div>
              )}
            </>
          </div>
        </td>
      </TableRow>
    </>
  );
};

export default TimeslotsListRow;
